"use client"
import styled from "@emotion/styled"
import { AllJobs, SingleJobsPage } from "lib/jobs"
import { useEffect, useState } from "react"
import { trackCareerRemoveFilter } from "reusable/gtm"
import colors from "theme/colors"
import {
  CustomBreadcrumbs,
  Desktop,
  HomeIcon,
  ImageContainer,
  Mobile,
  NewText,
  TitleWithLine,
} from "ui"
import { sharedColors } from "ui/theme/colors"
import { removePTags } from "utils/utils"
import CardGrid from "./CardGrid"
import ImageText from "./ImageText"
import JobsSearch, {
  CategoriesArr,
  CategoryState,
  OtherState,
} from "./JobsSearch"

const StyledIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
    ${(props) => props.theme.breakpoints.only("md")} {
    gap: 0px;
  }
`

const StyledBackground = styled.div`
  background-color: ${colors.deepBlue};
`
const StyledImage = styled.div`
  display: flex;
  justify-content: center;
  /* padding-left: 28px; */
  ${(props) => props.theme.breakpoints.down("md")} {
    /* padding-left: 28px; */
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    justify-content: end;
    padding-top: 3px;
  }
`
const StyledGrid = styled.div`
  display: grid;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 22px;
    // height: 555px;
    grid-template-columns: 1fr 1fr;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    // height: 840px;
    gap: 33px;
  }
`
const StyledFlex = styled.div`
  display: flex;
  flex-direction: column;
   padding-left: 15px;
   padding-right: 15px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-right: 0;
    padding-left: 146px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-left: 220px;
  }
  gap: 20px;
  ${(props) => props.theme.breakpoints.down("md")} {
    grid-row: 2/3;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    gap: 28px;
  }
   ${(props) => props.theme.breakpoints.up("lg")} {
      padding-bottom: 180px;   
  }
    ${(props) => props.theme.breakpoints.only("md")} {
      padding-bottom: 100px;  
  }
`

const BreadPadding = styled.div`
  ${(props) => props.theme.breakpoints.down("md")} {
    padding-left: 20px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 40px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-bottom: 80px;
  }
`

type Props = {
  page: SingleJobsPage
  jobs: AllJobs
  categoriesArr: CategoriesArr
  companyArray: string[]
  locationArray: string[]
}
function convertToState(categoriesArr: CategoriesArr): CategoryState {
  const initialCategories = categoriesArr
    .filter((el) => el.numberOfJobs > 0)
    .reduce((acc: CategoryState, category) => {
      acc[category.title] = { checked: true, id: category.id }
      return acc
    }, {})
  return initialCategories
}

function convertOtherToState(array: string[]) {
  return array.map((el) => ({ title: el, checked: true }))
}

function AllJobsPage({
  page,
  jobs,
  categoriesArr,
  companyArray,
  locationArray,
}: Props) {
  const [categoryState, setCategoryState] = useState(
    convertToState(categoriesArr),
  )
  const [companyState, setCompanyState] = useState<OtherState>(
    convertOtherToState(companyArray),
  )
  const [locationState, setLocationState] = useState<OtherState>(
    convertOtherToState(locationArray),
  )
  const [prevFiltersNumber, setPrevFiltersNumber] = useState(0)
  const scrollToSection = () => {
    const section = document.getElementById("jobs")
    if (section) {
      section.scrollIntoView({ behavior: "smooth" })

      // Adding a short delay before focusing
      setTimeout(() => {
        section.focus()
      }, 1000) // Adjust the timeout as needed for smooth scrolling duration
    }
  }
  const onCardClick = (id: number) => {
    scrollToSection()

    const updatedState = Object.fromEntries(
      Object.entries(categoryState).map(([key, value]) => {
        return [key, { ...value, checked: value.id === id }]
      }),
    )

    setCategoryState(updatedState)
  }
  useEffect(() => {
    // get current checked filters
    const checkedCategories = Object.keys(categoryState).filter(key => categoryState[key].checked)
    const checkedCompanies = companyState.filter(company => company.checked).map(cat => cat.title)
    const checkedLocations = locationState.filter(location => location.checked).map(loc => loc.title)

    const filterNumber =
      checkedCategories.length +
      checkedCompanies.length +
      checkedLocations.length

    const categoryString = checkedCategories.join(",")
    const companyString = checkedCompanies.join(",")
    const locationString = checkedLocations.join(",")

    trackCareerRemoveFilter(categoryString, companyString, locationString, filterNumber > prevFiltersNumber ? "career_add_filter" : "career_remove_filter")
    setPrevFiltersNumber(filterNumber)
  },
    [categoryState, companyState, locationState])
  return (
    <>
      <StyledBackground>
        <Mobile>
          <BreadPadding>
            <CustomBreadcrumbs
              current={{
                title: page.pageName || "",
                url: `/jobs`,
              }}
              parents={[{ title: <HomeIcon />, url: "/" }]}
            />
          </BreadPadding>
        </Mobile>
        <StyledGrid>
          <StyledFlex>
            <StyledIntro>
              <div>
                <Desktop>
                  <BreadPadding>
                    <CustomBreadcrumbs
                      current={{
                        title: page.pageName || "",
                        url: `/jobs`,
                      }}
                      parents={[{ title: <HomeIcon />, url: "/" }]}
                    />
                  </BreadPadding>
                </Desktop>
                <TitleWithLine
                  noPadding
                  component={"h1"}
                  titleColor={sharedColors.black}
                  title={removePTags(page.contentObj.headline || "")}
                  titleFontSize={42}
                  desktopLineHeight="48px"
                  mobileTitleFontSize={36}
                  mobileLineHeight="50.4px"
                  largeDesktopFontSize={64}
                  largeDesktopLineHeight="72px"

                />
              </div>
              <NewText
                component={"p"}
                largeDesktopFontSize={28}
                largeDesktopLineHeight="39.2px"
                desktopFontSize={18}
                desktopLineHeight={"25.2px"}
                mobileFontSize={18}
                mobileLineHeight={"24px"}
                dangerouslySetInnerHTML={{ __html: page.contentObj.body || "" }}
              >
              </NewText>
            </StyledIntro>
            <CardGrid
              categoryColors={page.categoryColors}
              sectionTitle={page.categoriesTitle}
              categories={categoriesArr}
              onCardClick={onCardClick}
            />
          </StyledFlex>

          {page.corpBanner && (
            <StyledImage>
              <ImageContainer
                desktopUrl={page.corpBanner}
                sm={true}
                mobileUrl={page.mobileImage || page.corpBanner}
                objectPosition="right top"
                fill
                height={914}
                width={994}
                mediumHeight={610}
                mediumWidth={662}
                mobileHeight={345}
                emptyAlt={true}
              />
            </StyledImage>
          )}
        </StyledGrid>
      </StyledBackground>
      <ImageText content={page.imageAndTextObjCorp} />
      <JobsSearch
        jobs={jobs}
        categoriesState={categoryState}
        setCategoryState={setCategoryState}
        companyState={companyState}
        setCompanyState={setCompanyState}
        locationState={locationState}
        setLocationState={setLocationState}
      />
    </>
  )
}

export default AllJobsPage
